import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2, Plus, ToggleLeft, ToggleRight, History, XCircle, Users } from 'lucide-react';

export const RiskHistory = ({ history, onClear }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-semibold">Cronologia Ricerche</h2>
      {history.length > 0 && (
        <Button variant="outline" size="sm" className="text-red-600" onClick={onClear}>
          <Trash2 className="w-4 h-4 mr-1" /> Azzera
        </Button>
      )}
    </div>
    {history.length === 0 ? (
      <p className="text-sm text-slate-400 text-center py-4">Nessuna ricerca effettuata</p>
    ) : (
      <div className="space-y-2">
        {history.map(h => (
          <div key={h.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <div className={`w-2 h-2 rounded-full ${h.success ? 'bg-emerald-500' : 'bg-red-500'}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800">{h.report_label}</p>
              <p className="text-xs text-slate-500">{h.codice_fiscale || h.partita_iva} — {h.created_by}</p>
            </div>
            <span className="text-xs text-slate-400">{new Date(h.created_at).toLocaleString('it-IT')}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const RiskBlacklist = ({ blacklist, blForm, setBlForm, onAdd, onRemove }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6">
    <h2 className="text-lg font-semibold mb-4">Lista Esclusione</h2>
    <p className="text-sm text-slate-500 mb-4">I soggetti in lista non potranno essere oggetto di ricerche Risk Intelligence.</p>

    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4 p-4 bg-slate-50 rounded-lg border border-dashed border-slate-300">
      <Input value={blForm.codice_fiscale} onChange={e => setBlForm(f => ({...f, codice_fiscale: e.target.value.toUpperCase()}))} placeholder="Codice Fiscale" className="uppercase" data-testid="bl-cf-input" />
      <Input value={blForm.nome} onChange={e => setBlForm(f => ({...f, nome: e.target.value}))} placeholder="Nome" data-testid="bl-nome-input" />
      <Input value={blForm.cognome} onChange={e => setBlForm(f => ({...f, cognome: e.target.value}))} placeholder="Cognome" data-testid="bl-cognome-input" />
      <Input value={blForm.motivo} onChange={e => setBlForm(f => ({...f, motivo: e.target.value}))} placeholder="Motivo (opzionale)" data-testid="bl-motivo-input" />
    </div>
    <Button onClick={onAdd} className="mb-4" data-testid="bl-add-btn">
      <Plus className="w-4 h-4 mr-1" /> Aggiungi a Esclusione
    </Button>

    {blacklist.length === 0 ? (
      <p className="text-sm text-slate-400 text-center py-4">Nessun soggetto in lista</p>
    ) : (
      <div className="space-y-2">
        {blacklist.map(item => (
          <div key={item.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
            <div>
              <p className="text-sm font-medium text-slate-800">
                {item.codice_fiscale && <span className="font-mono">{item.codice_fiscale}</span>}
                {item.codice_fiscale && (item.nome || item.cognome) && ' — '}
                {item.nome} {item.cognome}
              </p>
              {item.motivo && <p className="text-xs text-slate-500">{item.motivo}</p>}
            </div>
            <Button variant="ghost" size="sm" onClick={() => onRemove(item.id)}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const RiskAccessManager = ({ usersAccess, onToggle }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-semibold">Gestione Accesso Clienti</h2>
      <p className="text-xs text-slate-500">Abilita/disabilita l'accesso a Risk Intelligence per ogni cliente</p>
    </div>
    {usersAccess.length === 0 ? (
      <p className="text-sm text-slate-400 text-center py-4">Nessun cliente registrato</p>
    ) : (
      <div className="space-y-2 max-h-[300px] overflow-y-auto">
        {usersAccess.map(u => (
          <div key={u.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${u.risk_access ? 'bg-emerald-500' : 'bg-slate-300'}`} />
              <div>
                <p className="text-sm font-medium text-slate-800">{u.nome} {u.cognome}</p>
                <p className="text-xs text-slate-500">{u.email}{u.azienda ? ` — ${u.azienda}` : ''}</p>
              </div>
            </div>
            <button
              onClick={() => onToggle(u.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                u.risk_access
                  ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                  : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
              }`}
              data-testid={`risk-access-${u.id}`}
            >
              {u.risk_access ? <><ToggleRight className="w-4 h-4 inline mr-1" />Abilitato</> : <><ToggleLeft className="w-4 h-4 inline mr-1" />Disabilitato</>}
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);
